import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Drawer, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Context
import { AccountProvider, useAccount } from "./context/AccountContext";

// Components
import { NavContent } from "./components/NavContent";
import { AccountHeader } from "./components/AccountHeader";
import { GlobalLoadingOverlay } from "./components/shared/GlobalLoadingOverlay";
import { ErrorBanner } from "./components/shared/ErrorBanner";
import { PausedPlanBanner } from "./components/shared/PausedPlanBanner";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";
import { EnhancedDogEditModal } from "./components/DogEditor/EnhancedDogEditModal";
import { NutritionSummaryModal } from "./components/NutritionSummary/NutritionSummaryModal";
import { MealEditorModal } from "./components/MealEditor/MealEditorModal";
import { BasketModal } from "./components/Basket/BasketModal";
import { SkipConfirmDialog } from "./components/shared/SkipConfirmDialog";
import { DeliveryDateModal } from "./components/DeliveryDateModal";
import { DailyServingModal } from "./components/DailyServingModal";
import { ChangeFrequencyModal } from "./components/ChangeFrequencyModal";
import { StopPlanModal } from "./components/StopPlanModal";
import { ChangeQuantityModal } from "./components/ChangeQuantityModal";
import {
  StartPlanPromptModal,
  wasResumePromptDismissedThisSession,
  markResumePromptDismissed,
  clearResumePromptDismissal,
} from "./components/StartPlanPromptModal";
import type { BundlePlan, LegacySubscription } from "./utils/recharge";
import type { AvailableDeliveryDate } from "./api/accountProxy";
import { fetchAvailableDeliveryDates, changeDeliveryDate, changeDailyServing, changeDeliveryFrequency, cancelSubscription, changeQuantity, getOnetimesForAddress } from "./api/accountProxy";
import {
  parseBundlePlan,
  parseLegacySubscription,
  estimateDeliveryDate,
  isActiveSub,
  isBundleSub,
  buildVariantIndex,
} from "./utils/recharge";
import { resumeSubscription } from "./api/accountProxy";
import { DevPersonaBar } from "./components/DevPersonaBar";
import { buildPersonaData, type PersonaKey } from "./utils/personaPreview";
// Pages
import { DashboardPage } from "./pages/DashboardPage";
import { HelpPage } from "./pages/HelpPage";
import { SubscriptionsPage } from "./pages/SubscriptionsPage";
import { DogsPage } from "./pages/DogsPage";
import { DogProfilePage } from "./pages/DogProfilePage";
import { DogEditPage } from "./pages/DogEditPage";
import { UpcomingDeliveriesPage } from "./pages/UpcomingDeliveriesPage";
import { OrderHistoryPage } from "./pages/OrderHistoryPage";
import { DeliveryAddressPage } from "./pages/DeliveryAddressPage";
import { PaymentMethodsPage } from "./pages/PaymentMethodsPage";
import { ExtrasPage } from "./pages/ExtrasPage";
import { DevPage } from "./components/DevPage/DevPage";

// Types
import type { NutritionRecalculationResult } from "./types";
import { QUIZ } from "@/theme/quizTokens";

const HEADER_H = 72;
const SIDEBAR_W = 300;
const SIDEBAR_W_COLLAPSED = 84;

/**
 * Inner content component that uses the AccountContext
 */
function AccountContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // State from context
  const {
    customer,
    dogs,
    activeDogs,
    subscriptions,
    upcoming,
    orders,
    addresses,
    payments,
    products,
    hasSubs,
    activeSubsCount,
    nextDeliveryDate,
    loading,
    globalLoading,
    globalLoadingMessage,
    showLoading,
    hideLoading,
    refresh,
    nav,
    setNav,
    isDevAccessible,
    isAdminEmail,
    getDogById,
    saveDogWithNutrition,
    softDeleteDog,
    restoreDog,
    sendPaymentUpdateEmail,
    skipDelivery,
    basket,
    basketItemCount,
    addToBasket,
    clearBasket,
    removeFromBasket,
    checkoutBasket,
    effectiveShop,
    effectiveProxyBase,
    addError,
    selectedDogId,
    setSelectedDogId,
  } = useAccount();

  // Get primary address ID (first address with an active subscription)
  const primaryAddressId = useMemo(() => {
    // Get address IDs that have active subscriptions
    const activeSubAddressIds = new Set(
      subscriptions
        .filter((s: any) => s.status === "active")
        .map((s: any) => String(s.address_id))
    );
    // Return the first address ID that has an active subscription
    const matchingAddress = addresses.find((a: any) => activeSubAddressIds.has(String(a.id)));
    return matchingAddress ? String(matchingAddress.id) : addresses[0]?.id ? String(addresses[0].id) : null;
  }, [subscriptions, addresses]);

  // Pending one-times state (used by UpcomingDeliveriesPage)
  const [pendingOnetimes, setPendingOnetimes] = useState<any[]>([]);

  const fetchPendingOnetimes = async () => {
    if (!primaryAddressId) return;
    try {
      const result = await getOnetimesForAddress(effectiveProxyBase, effectiveShop, primaryAddressId, customer?.id ? String(customer.id) : undefined);
      if (result.ok) {
        setPendingOnetimes(result.onetimes || []);
      }
    } catch {}
  };

  useEffect(() => {
    fetchPendingOnetimes();
  }, [primaryAddressId, effectiveProxyBase, effectiveShop, customer?.id]);

  // Fetch delivery cutoff hours from Qivora on mount
  useEffect(() => {
    if (!effectiveShop) return;
    fetchAvailableDeliveryDates("", effectiveShop, 4).then((result) => {
      if (result.ok) {
        setDeliveryCutoffHours(result.cutoffHours);
        setDeliveryTimezone(result.timezone);
        setDeliveryDayNames(result.deliveryDayNames);
      }
    }).catch(() => {});
  }, [effectiveShop]);

  // Local UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editDogId, setEditDogId] = useState<string | null>(null);
  const [nutritionResult, setNutritionResult] =
    useState<NutritionRecalculationResult | null>(null);
  const [editMealsPlan, setEditMealsPlan] = useState<BundlePlan | LegacySubscription | null>(null);
  const [basketModalOpen, setBasketModalOpen] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [skipTarget, setSkipTarget] = useState<{
    subscriptionId: string;
    addressId: string;
    title: string;
    nextChargeDate: string | null;
  } | null>(null);
  const [skipping, setSkipping] = useState(false);
  const [skipSuccess, setSkipSuccess] = useState(false);
  const [skipNewDeliveryDate, setSkipNewDeliveryDate] = useState<string | null>(null);

  // Delivery date modal state
  const [deliveryDateModalOpen, setDeliveryDateModalOpen] = useState(false);
  const [deliveryDateTarget, setDeliveryDateTarget] = useState<{
    subscriptionId: string;
    addressId?: string;
    currentDeliveryDate?: string;
  } | null>(null);
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState<AvailableDeliveryDate[]>([]);
  const [deliveryDatesLoading, setDeliveryDatesLoading] = useState(false);
  const [deliveryDatesError, setDeliveryDatesError] = useState<string | undefined>(undefined);
  const [deliveryCutoffHours, setDeliveryCutoffHours] = useState<number>(36);
  const [deliveryTimezone, setDeliveryTimezone] = useState<string>("Europe/London");
  const [deliveryDayNames, setDeliveryDayNames] = useState<string[]>([]);

  // Daily serving modal state
  const [dailyServingModalOpen, setDailyServingModalOpen] = useState(false);
  const [dailyServingTarget, setDailyServingTarget] = useState<any | null>(null);

  // Change frequency modal state
  const [frequencyModalOpen, setFrequencyModalOpen] = useState(false);
  const [frequencyTarget, setFrequencyTarget] = useState<any | null>(null);

  // Stop plan modal state
  const [stopPlanModalOpen, setStopPlanModalOpen] = useState(false);
  const [stopPlanTarget, setStopPlanTarget] = useState<{ subscriptionId: string; dogName?: string } | null>(null);

  // Start-a-plan / resume-plan prompt.
  // - No subscription data at all (deleted, or brand new) -> hard-gated quiz prompt.
  // - Cancelled/paused BUNDLE subs, none active -> dismissible resume prompt.
  // - Legacy-only non-active subs -> no prompt.
  // - Admin emails bypass everything.
  const activeSubsList = useMemo(
    () => subscriptions.filter(isActiveSub),
    [subscriptions]
  );
  const resumableSubs = useMemo(
    () =>
      subscriptions.filter(
        (s: any) =>
          String(s?.status || "").toLowerCase() === "cancelled" && isBundleSub(s)
      ),
    [subscriptions]
  );
  const [resumePromptDismissed, setResumePromptDismissed] = useState(
    wasResumePromptDismissedThisSession()
  );

  const startPlanPromptMode: "quiz" | "resume" | null = useMemo(() => {
    if (loading || isAdminEmail) return null;
    if (activeSubsCount > 0) return null;
    if (subscriptions.length === 0) return "quiz";
    if (resumableSubs.length > 0 && !resumePromptDismissed) return "resume";
    return null;
  }, [
    loading,
    isAdminEmail,
    activeSubsCount,
    subscriptions.length,
    resumableSubs.length,
    resumePromptDismissed,
  ]);

  const handleResumePlans = useCallback(
    async (deliveryDate: string | null = null): Promise<{ ok: boolean; error?: string }> => {
      for (const sub of resumableSubs) {
        const result = await resumeSubscription(
          effectiveProxyBase,
          effectiveShop || "",
          String((sub as any).id),
          customer?.id ? String(customer.id) : undefined
        );
        if (!result.ok) {
          return { ok: false, error: result.error };
        }
      }

      // Apply the chosen first-delivery date (once per address; the reschedule
      // moves the whole address's schedule chain)
      if (deliveryDate) {
        const seenAddresses = new Set<string>();
        for (const sub of resumableSubs) {
          const addressId = String((sub as any).address_id || "");
          if (addressId && seenAddresses.has(addressId)) continue;
          if (addressId) seenAddresses.add(addressId);
          const rescheduled = await changeDeliveryDate(
            "",
            effectiveShop || "",
            String((sub as any).id),
            deliveryDate,
            undefined,
            (sub as any).address_id
          );
          if (!rescheduled.ok) {
            await refresh();
            return {
              ok: false,
              error:
                "Your plan is resumed, but we couldn't set that delivery date. You can change it from the Subscriptions page.",
            };
          }
        }
      }

      await refresh();
      return { ok: true };
    },
    [resumableSubs, effectiveProxyBase, effectiveShop, customer?.id, refresh]
  );

  const handleLoadResumeDates = useCallback(async () => {
    const result = await fetchAvailableDeliveryDates("", effectiveShop || "", 4);
    return {
      ok: result.ok,
      availableDates: result.availableDates || [],
      error: result.error,
    };
  }, [effectiveShop]);

  // Overview of what each paused plan contains, shown inside the resume modal
  // so customers see exactly what comes back when they resume.
  const pausedPlanOverviews = useMemo(() => {
    if (!resumableSubs.length) return [];
    const variantIndex = buildVariantIndex(products);
    return resumableSubs.map((sub: any) => {
      const plan = parseBundlePlan(sub);
      let dogName: string | null = null;
      try {
        const dogRaw = sub?.properties?.find((p: any) => p?.name === "_paws_dog_json")?.value;
        dogName = dogRaw ? JSON.parse(String(dogRaw))?.name || null : null;
      } catch {}
      return {
        dogName,
        planDays: plan.planDays,
        packSizeGrams: plan.packSizeGrams,
        items: plan.meals
          .filter((m) => Number(m.count) > 0)
          .map((m) => {
            const info = variantIndex.get(String(m.variantId));
            return {
              title: info?.productTitle || "Recipe",
              imageUrl: info?.imageUrl || null,
              count: Number(m.count),
            };
          }),
      };
    });
  }, [resumableSubs, products]);

  // Explicitly requested via the "Resume my plan" button — opens the resume
  // modal even for admin emails (which bypass only the automatic popup).
  const [resumePromptForced, setResumePromptForced] = useState(false);

  const handleDismissResumePrompt = useCallback(() => {
    markResumePromptDismissed();
    setResumePromptDismissed(true);
    setResumePromptForced(false);
  }, []);

  const handleReopenResumePrompt = useCallback(() => {
    clearResumePromptDismissal();
    setResumePromptDismissed(false);
    setResumePromptForced(true);
  }, []);

  // Admin-only dashboard persona preview (testing view)
  const [previewPersona, setPreviewPersona] = useState<PersonaKey>("real");
  const personaData = useMemo(
    () => (isAdminEmail ? buildPersonaData(previewPersona, products) : null),
    [isAdminEmail, previewPersona, products]
  );

  // Change quantity modal state (for legacy subscriptions)
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [quantityTarget, setQuantityTarget] = useState<{
    subscriptionId: string;
    productTitle: string;
    variantTitle: string;
    currentQuantity: number;
    pricePerUnit: number;
  } | null>(null);

  const editDog = editDogId ? getDogById(editDogId) : null;

  // Dog navigation helpers
  const handleViewDog = (dogId: string) => { setSelectedDogId(dogId); setNav("dog-profile"); };
  const handleEditDog = (dogId: string) => { setSelectedDogId(dogId); setNav("dog-edit"); };
  const handleBackToDogs = () => { setSelectedDogId(null); setNav("dogs"); };

  // Handlers
  const onToggleCollapse = () => setSidebarCollapsed((v) => !v);
  const onOpenMobileSidebar = () => setSidebarOpen(true);

  // The subscription's _paws_dog_json is a purchase-time snapshot; the
  // customer's current dog profile (metafield) is the source of truth for
  // allergies. Prefer it when the dog is linked to the plan.
  const freshAllergiesForPlan = (plan: BundlePlan | LegacySubscription): string[] => {
    const linked = dogs.find(
      (d) => String(d.subscription?.subscriptionId ?? "") === String(plan.subscriptionId)
    );
    return linked?.allergies ?? plan.dogAllergies ?? [];
  };

  const onEditMeals = (plan: BundlePlan | LegacySubscription) => {
    setEditMealsPlan({ ...plan, dogAllergies: freshAllergiesForPlan(plan) });
  };


  const onEditMealsFromCharge = (charge: any) => {
    // Find subscription from charge line items
    const lineItems = charge?.line_items || [];
    if (lineItems.length === 0) return;

    // Get subscription_id from first line item
    const subscriptionId = String(lineItems[0]?.subscription_id || "");
    if (!subscriptionId) return;

    // Find matching subscription
    const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
    if (!subscription) return;

    // Parse as bundle or legacy
    const isBundleSub = subscription.properties?.some((p: any) => p.name === "_paws_meals_json");
    if (isBundleSub) {
      const plan = parseBundlePlan(subscription);
      setEditMealsPlan(plan);
    } else {
      const legacy = parseLegacySubscription(subscription);
      setEditMealsPlan(legacy);
    }
  };

  const handleAddToBasket = (changes: any[]) => {
    if (!editMealsPlan) return;
    addToBasket(editMealsPlan.subscriptionId, changes);
    setEditMealsPlan(null);
    setBasketModalOpen(true);
  };

  const handleCheckout = async () => {
    // Daily grams are auto-recalculated by the proxy worker based on total meals
    // Formula: (totalPacks × packSize) / planDays
    await checkoutBasket();
  };

  const handleSaveDog = async (formData: any) => {
    if (!editDogId) return;

    try {
      // Save dog with automatic nutrition recalculation
      const result = await saveDogWithNutrition(editDogId, formData);

      // Close edit modal
      setEditDogId(null);

      // If nutrition was recalculated, show summary modal
      if (result) {
        setNutritionResult(result);
      }
    } catch (error: any) {
      console.error("[handleSaveDog] Error saving dog:", error);
      addError({
        message: error?.message || "Failed to save dog profile",
        context: "Dog Edit",
        code: "SAVE_DOG_FAILED",
      });
      // Still close the modal on error
      setEditDogId(null);
    }
  };

  const handleSkipDelivery = (subscriptionId: string) => {
    // Find the subscription to get details
    const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
    if (!subscription) return;

    // Derive delivery date from charge date: charge + cutoff hours → next available day
    const addressId = String((subscription as any)?.address_id || "");
    let deliveryDate: string | null = null;

    if (subscription.next_charge_scheduled_at) {
      deliveryDate = estimateDeliveryDate(subscription.next_charge_scheduled_at, deliveryCutoffHours, deliveryDayNames);
    }

    setSkipTarget({
      subscriptionId,
      addressId,
      title: subscription.product_title || "Subscription",
      nextChargeDate: deliveryDate,
    });
    setSkipConfirmOpen(true);
  };

  const handleConfirmSkip = async () => {
    if (!skipTarget) return;

    setSkipping(true);
    try {
      const result = await skipDelivery(skipTarget.subscriptionId, skipTarget.addressId);
      setSkipSuccess(true);
      setSkipNewDeliveryDate(result.newDeliveryDate || null);
    } catch (error: any) {
      addError({
        message: error?.message || "Failed to skip delivery. Please try again.",
        context: "Skip Delivery",
        code: "SKIP_DELIVERY_FAILED",
      });
      setSkipConfirmOpen(false);
    } finally {
      setSkipping(false);
    }
  };

  const handleCloseSkipDialog = () => {
    setSkipConfirmOpen(false);
    setSkipTarget(null);
    setSkipSuccess(false);
    setSkipNewDeliveryDate(null);
  };

  // Delivery Date Handlers
  const handleOpenDeliveryDateModal = async (subscriptionId: string) => {
    // Find subscription to get address_id
    const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
    const addressId = String((subscription as any)?.address_id || "");

    // Derive delivery date from charge date: charge + cutoff hours → next available day
    let currentDeliveryDate: string | undefined;
    if (subscription?.next_charge_scheduled_at) {
      currentDeliveryDate = estimateDeliveryDate(subscription.next_charge_scheduled_at, deliveryCutoffHours, deliveryDayNames) ?? undefined;
    }

    // Check if the next charge date is still in the future (hasn't been charged yet)
    const nextChargeDate = subscription?.next_charge_scheduled_at;
    const hasUpcomingCharge = nextChargeDate && new Date(nextChargeDate) > new Date();

    if (!hasUpcomingCharge) {
      // Already charged or no upcoming charge - show error message
      setDeliveryDateTarget({ subscriptionId, addressId, currentDeliveryDate });
      setDeliveryDateModalOpen(true);
      setDeliveryDatesLoading(false);
      setDeliveryDatesError(
        "Your current order has already been charged. You can change the delivery date for your next order after this one is processed."
      );
      setAvailableDeliveryDates([]);
      return;
    }

    setDeliveryDateTarget({
      subscriptionId,
      addressId,
      currentDeliveryDate,
    });
    setDeliveryDateModalOpen(true);
    setDeliveryDatesLoading(true);
    setDeliveryDatesError(undefined);
    setAvailableDeliveryDates([]);

    try {
      // Fetch 26 weeks (6 months) of available dates
      const result = await fetchAvailableDeliveryDates(
        effectiveProxyBase,
        effectiveShop,
        26,
        customer?.id ? String(customer.id) : undefined
      );
      if (result.ok) {
        setAvailableDeliveryDates(result.availableDates);
        setDeliveryCutoffHours(result.cutoffHours);
        setDeliveryTimezone(result.timezone);
        setDeliveryDayNames(result.deliveryDayNames);
      } else {
        setDeliveryDatesError(result.error || "Failed to load available dates");
      }
    } catch (err: any) {
      setDeliveryDatesError(err?.message || "Failed to load available dates");
    } finally {
      setDeliveryDatesLoading(false);
    }
  };

  const handleConfirmDeliveryDate = async (newDate: string) => {
    if (!deliveryDateTarget) return;

    // Routes through Qivora for centralised delivery date management
    const result = await changeDeliveryDate(
      effectiveProxyBase,
      effectiveShop,
      deliveryDateTarget.subscriptionId,
      newDate,
      customer?.id ? String(customer.id) : undefined,
      deliveryDateTarget.addressId
    );

    if (!result.ok) {
      throw new Error(result.error || "Failed to update delivery date");
    }
    // Don't refresh here - modal auto-closes with success flag, refresh happens in close handler
  };

  const handleCloseDeliveryDateModal = (success?: boolean) => {
    setDeliveryDateModalOpen(false);
    setDeliveryDateTarget(null);
    setAvailableDeliveryDates([]);
    setDeliveryDatesError(undefined);
    if (success) {
      refresh();
    }
  };

  // Daily Serving Handlers
  const handleOpenDailyServingModal = (subscriptionId: string) => {
    // Find the subscription
    const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
    if (!subscription) return;

    setDailyServingTarget(subscription);
    setDailyServingModalOpen(true);
  };

  const handleConfirmDailyServing = async (
    newDailyGrams: number,
    newPackSizeGrams: number,
    newVariantId: string,
    meals: Array<{ variantId: string; count: number }>,
    newPrice: number,
    mealsSummary: string
  ) => {
    if (!dailyServingTarget) return;

    // Note: Modal handles its own loading/success state, no global overlay needed
    const result = await changeDailyServing(
      effectiveProxyBase,
      effectiveShop,
      String(dailyServingTarget.id),
      newDailyGrams,
      newPackSizeGrams,
      newVariantId,
      meals,
      newPrice,
      mealsSummary,
      customer?.id ? String(customer.id) : undefined
    );

    if (!result.ok) {
      throw new Error(result.error || "Failed to update daily serving");
    }
    // Don't refresh here - let modal close first, refresh happens in close handler
  };

  const handleCloseDailyServingModal = (success?: boolean) => {
    setDailyServingModalOpen(false);
    setDailyServingTarget(null);
    if (success) {
      refresh();
    }
  };

  // Change Frequency Handlers
  const handleOpenFrequencyModal = (subscriptionId: string) => {
    const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
    if (!subscription) return;

    setFrequencyTarget(subscription);
    setFrequencyModalOpen(true);
  };

  const handleConfirmFrequencyChange = async (
    newPlanDays: number,
    newPackSizeGrams: number,
    newVariantId: string,
    meals: Array<{ variantId: string; count: number }>,
    newPrice: number,
    mealsSummary: string
  ) => {
    if (!frequencyTarget) return;

    // Note: Modal handles its own loading/success state, no global overlay needed
    const result = await changeDeliveryFrequency(
      effectiveProxyBase,
      effectiveShop,
      String(frequencyTarget.id),
      newPlanDays,
      newPackSizeGrams,
      newVariantId,
      meals,
      newPrice,
      mealsSummary,
      customer?.id ? String(customer.id) : undefined
    );

    if (!result.ok) {
      throw new Error(result.error || "Failed to update delivery frequency");
    }
    // Don't refresh here - let modal close first, refresh happens in close handler
  };

  const handleCloseFrequencyModal = (success?: boolean) => {
    setFrequencyModalOpen(false);
    setFrequencyTarget(null);
    if (success) {
      refresh();
    }
  };

  // Stop Plan Handlers
  const handleOpenStopPlanModal = (subscriptionId: string) => {
    const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
    if (!subscription) return;

    // Try to get dog name from subscription properties
    let dogName: string | undefined;
    const props = (subscription as any).properties || [];
    const dogJsonProp = props.find((p: any) => p.name === "_paws_dog_json");
    if (dogJsonProp?.value) {
      try {
        const dog = JSON.parse(dogJsonProp.value);
        dogName = dog.name;
      } catch (err) {
        console.warn("[handleOpenStopPlanModal] Failed to parse dog JSON:", err);
        // Continue without dog name - this is non-critical
      }
    }

    setStopPlanTarget({ subscriptionId, dogName });
    setStopPlanModalOpen(true);
  };

  const handleConfirmStopPlan = async (reason: string, feedback: string) => {
    if (!stopPlanTarget) return;

    // Note: Modal handles its own loading/success state, no global overlay needed
    const result = await cancelSubscription(
      effectiveProxyBase,
      effectiveShop,
      stopPlanTarget.subscriptionId,
      reason,
      feedback,
      customer?.id ? String(customer.id) : undefined
    );

    if (!result.ok) {
      throw new Error(result.error || "Failed to cancel subscription");
    }
    // Don't refresh here - let modal close first, refresh happens in close handler
  };

  const handleCloseStopPlanModal = (success?: boolean) => {
    setStopPlanModalOpen(false);
    setStopPlanTarget(null);
    if (success) {
      refresh();
    }
  };

  // Change Quantity Handlers (for legacy subscriptions)
  const handleOpenQuantityModal = (subscriptionId: string) => {
    const subscription = subscriptions.find(s => String(s.id) === subscriptionId);
    if (!subscription) return;

    // Calculate price per unit from total price / quantity
    const totalPrice = parseFloat(String(subscription.price || "0"));
    const quantity = parseInt(String(subscription.quantity || 1), 10);
    const pricePerUnit = quantity > 0 ? totalPrice / quantity : totalPrice;

    setQuantityTarget({
      subscriptionId,
      productTitle: subscription.product_title || "Product",
      variantTitle: subscription.variant_title || "",
      currentQuantity: quantity,
      pricePerUnit,
    });
    setQuantityModalOpen(true);
  };

  const handleConfirmQuantityChange = async (newQuantity: number) => {
    if (!quantityTarget) return;

    // Note: Modal handles its own loading/success state, no global overlay needed
    const result = await changeQuantity(
      effectiveProxyBase,
      effectiveShop,
      quantityTarget.subscriptionId,
      newQuantity,
      customer?.id ? String(customer.id) : undefined
    );

    if (!result.ok) {
      throw new Error(result.error || "Failed to update quantity");
    }
    // Don't refresh here - let modal close first, refresh happens in close handler
  };

  const handleCloseQuantityModal = (success?: boolean) => {
    setQuantityModalOpen(false);
    setQuantityTarget(null);
    if (success) {
      refresh();
    }
  };

  // Loading state - show global overlay for initial load
  // Keep loading visible until all bootstrap data is loaded (prevents "-" placeholders)
  if (loading) {
    return (
      <GlobalLoadingOverlay
        visible={true}
        message="Loading your account..."
      />
    );
  }

  return (
    <Box
      sx={{
        background: QUIZ.color.cream2,
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          lg: sidebarCollapsed
            ? `${SIDEBAR_W_COLLAPSED}px 1fr`
            : `${SIDEBAR_W}px 1fr`,
        },
      }}
    >
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Box
          sx={{
            height: "100%",
            background: "#fff",
            boxShadow: "0px 10px 60px rgba(226, 236, 249, 0.5)",
            position: "relative",
            overflow: "visible",
            zIndex: 50000,
          }}
        >
          <NavContent
            nav={nav}
            setNav={setNav}
            collapsed={sidebarCollapsed}
            onToggleCollapse={onToggleCollapse}
          />
        </Box>
      )}

      {/* Right area: header + scroll body */}
      <Box
        sx={{
          minWidth: 0,
          display: "grid",
          gridTemplateRows: `${HEADER_H}px 1fr`,
          minHeight: "100vh",
        }}
      >
        <AccountHeader
          customer={customer}
          onToggleSidebar={onOpenMobileSidebar}
          isMobile={isMobile}
          basketItemCount={basketItemCount}
          onOpenBasket={() => setBasketModalOpen(true)}
        />
        <Box
          sx={{
            minHeight: 0,
            overflow: "auto",
            background: QUIZ.color.cream2,
            p: { xs: 2, md: 4 },
          }}
        >

          {/* Error Banner - shows any tracked errors */}
          <ErrorBanner />

          {/* Issue #55: stopgap banner for fully-paused customers */}
          <PausedPlanBanner />

          {/* Pages */}
          {nav === "dashboard" && (
            <>
              {isAdminEmail && (
                <DevPersonaBar value={previewPersona} onChange={setPreviewPersona} />
              )}
              <DashboardPage
                customerFirstName={customer?.first_name || (customer as any)?.firstName || null}
                dogs={personaData ? personaData.dogs : activeDogs}
                nextDeliveryDate={personaData ? personaData.nextDeliveryDate : nextDeliveryDate}
                hasSubs={personaData ? personaData.subscriptions.length > 0 : hasSubs}
                activeSubsCount={
                  personaData
                    ? personaData.subscriptions.filter(isActiveSub).length
                    : activeSubsCount
                }
                products={products}
                subscriptions={personaData ? personaData.subscriptions : subscriptions}
                pausedSubs={
                  personaData
                    ? personaData.subscriptions.filter((s: any) => !isActiveSub(s) && isBundleSub(s))
                    : resumableSubs
                }
                onResume={personaData ? () => {} : handleReopenResumePrompt}
                onGoSubscriptions={() => setNav("subscriptions")}
                onGoDogs={() => setNav("dogs")}
                onGoUpcoming={() => setNav("upcoming")}
              />
            </>
          )}

          {nav === "subscriptions" && (
            <>
              {isAdminEmail && (
                <DevPersonaBar value={previewPersona} onChange={setPreviewPersona} />
              )}
              <SubscriptionsPage
                hasSubs={personaData ? personaData.subscriptions.length > 0 : hasSubs}
                subs={personaData ? personaData.subscriptions.filter(isActiveSub) : activeSubsList}
                upcoming={personaData ? [] : upcoming}
                orders={personaData ? [] : orders}
                addresses={personaData ? personaData.addresses : addresses}
                products={products}
                cutoffHours={deliveryCutoffHours}
                deliveryDayNames={deliveryDayNames}
                timezone={deliveryTimezone}
                canResume={
                  personaData
                    ? personaData.subscriptions.some((s: any) => !isActiveSub(s) && isBundleSub(s))
                    : resumableSubs.length > 0
                }
                pausedSubs={
                  personaData
                    ? personaData.subscriptions.filter((s: any) => !isActiveSub(s) && isBundleSub(s))
                    : resumableSubs
                }
                onResume={personaData ? () => {} : handleReopenResumePrompt}
                onEditMeals={onEditMeals}
                onSkipDelivery={handleSkipDelivery}
                onChangeDeliveryDate={handleOpenDeliveryDateModal}
                onChangeFrequency={handleOpenFrequencyModal}
                onChangeDailyServing={handleOpenDailyServingModal}
                onChangeQuantity={handleOpenQuantityModal}
                onStopPlan={handleOpenStopPlanModal}
              />
            </>
          )}

          {nav === "extras" && (
            <ExtrasPage
              subs={subscriptions}
              products={products}
              proxyBase={effectiveProxyBase}
              shop={effectiveShop}
              customerId={customer?.id ? String(customer.id) : undefined}
              onOnetimesChanged={fetchPendingOnetimes}
            />
          )}

          {nav === "upcoming" && (
            <UpcomingDeliveriesPage
              upcoming={upcoming}
              addresses={addresses}
              products={products}
              subscriptions={subscriptions}
              pendingOnetimes={pendingOnetimes}
              cutoffHours={deliveryCutoffHours}
              deliveryDayNames={deliveryDayNames}
              onEditMeals={onEditMealsFromCharge}
            />
          )}

          {nav === "orders" && (
            <OrderHistoryPage
              orders={orders}
              products={products}
              subscriptions={subscriptions}
            />
          )}

          {nav === "address" && (
            <DeliveryAddressPage
              orders={orders}
              upcoming={upcoming}
            />
          )}

          {nav === "dogs" && (
            <DogsPage
              dogs={dogs}
              onViewDog={handleViewDog}
              onEditDog={handleEditDog}
              onDeleteDog={async (dogId) => {
                try {
                  await softDeleteDog(dogId);
                } catch (err: any) {
                  addError({
                    message: err?.message || "Failed to remove dog",
                    context: "Remove Dog",
                    code: "SOFT_DELETE_DOG_FAILED",
                  });
                }
              }}
              onRestoreDog={async (dogId) => {
                try {
                  await restoreDog(dogId);
                } catch (err: any) {
                  addError({
                    message: err?.message || "Failed to restore dog",
                    context: "Restore Dog",
                    code: "RESTORE_DOG_FAILED",
                  });
                }
              }}
            />
          )}

          {nav === "dog-profile" && (
            <DogProfilePage
              dog={selectedDogId ? getDogById(selectedDogId) : undefined}
              onBack={handleBackToDogs}
              onEdit={handleEditDog}
              onDelete={async (dogId) => {
                try {
                  await softDeleteDog(dogId);
                } catch (err: any) {
                  addError({
                    message: err?.message || "Failed to remove dog",
                    context: "Remove Dog",
                    code: "SOFT_DELETE_DOG_FAILED",
                  });
                }
              }}
              onRestore={async (dogId) => {
                try {
                  await restoreDog(dogId);
                } catch (err: any) {
                  addError({
                    message: err?.message || "Failed to restore dog",
                    context: "Restore Dog",
                    code: "RESTORE_DOG_FAILED",
                  });
                }
              }}
              canDelete={activeDogs.length > 1}
            />
          )}

          {nav === "dog-edit" && (
            <DogEditPage
              dog={selectedDogId ? getDogById(selectedDogId) : undefined}
              onBack={() => setNav("dog-profile")}
              onSave={async (formData) => {
                if (!selectedDogId) return;
                try {
                  const result = await saveDogWithNutrition(selectedDogId, formData);
                  if (result) setNutritionResult(result);
                  setNav("dog-profile");
                } catch (error: any) {
                  console.error("[DogEditPage] Error saving dog:", error);
                  addError({
                    message: error?.message || "Failed to save dog profile",
                    context: "Dog Edit",
                    code: "SAVE_DOG_FAILED",
                  });
                }
              }}
            />
          )}

          {nav === "help" && <HelpPage onNavigate={setNav} />}

          {nav === "payments" && (
            <PaymentMethodsPage
              payments={payments}
              onSendUpdateEmail={sendPaymentUpdateEmail}
            />
          )}

          {nav === "dev" && isDevAccessible && <DevPage />}
        </Box>
      </Box>

      {/* Enhanced Dog Edit Modal */}
      <EnhancedDogEditModal
        open={!!editDogId}
        dog={editDog || null}
        onClose={() => setEditDogId(null)}
        onSave={handleSaveDog}
      />

      {/* Nutrition Summary Modal */}
      <NutritionSummaryModal
        open={!!nutritionResult}
        result={nutritionResult}
        onClose={() => setNutritionResult(null)}
        onGoToSubscriptions={() => {
          setNutritionResult(null);
          setNav("subscriptions");
        }}
      />

      {/* Meal Editor Modal */}
      <MealEditorModal
        open={!!editMealsPlan}
        onClose={() => setEditMealsPlan(null)}
        plan={editMealsPlan}
        products={products}
        onAddToBasket={handleAddToBasket}
      />

      {/* Basket Modal */}
      <BasketModal
        open={basketModalOpen}
        onClose={() => setBasketModalOpen(false)}
        basket={basket}
        onRemoveItem={removeFromBasket}
        onClearBasket={clearBasket}
        onCheckout={handleCheckout}
      />

      {/* Skip Confirm Dialog */}
      <SkipConfirmDialog
        open={skipConfirmOpen}
        onClose={handleCloseSkipDialog}
        onConfirm={handleConfirmSkip}
        nextChargeDate={skipTarget?.nextChargeDate || null}
        subscriptionTitle={skipTarget?.title || ""}
        loading={skipping}
        success={skipSuccess}
        newDeliveryDate={skipNewDeliveryDate}
      />

      {/* Delivery Date Modal */}
      <DeliveryDateModal
        open={deliveryDateModalOpen}
        onClose={handleCloseDeliveryDateModal}
        currentDeliveryDate={deliveryDateTarget?.currentDeliveryDate}
        availableDates={availableDeliveryDates}
        loading={deliveryDatesLoading}
        loadingError={deliveryDatesError}
        onConfirm={handleConfirmDeliveryDate}
        deliveryDayNames={deliveryDayNames}
      />

      {/* Daily Serving Modal */}
      <DailyServingModal
        open={dailyServingModalOpen}
        onClose={handleCloseDailyServingModal}
        subscription={dailyServingTarget}
        products={products}
        onConfirm={handleConfirmDailyServing}
      />

      {/* Change Frequency Modal */}
      <ChangeFrequencyModal
        open={frequencyModalOpen}
        onClose={handleCloseFrequencyModal}
        subscription={frequencyTarget}
        products={products}
        onConfirm={handleConfirmFrequencyChange}
      />

      {/* Stop Plan Modal */}
      <StopPlanModal
        open={stopPlanModalOpen}
        onClose={handleCloseStopPlanModal}
        subscriptionId={stopPlanTarget?.subscriptionId || ""}
        dogName={stopPlanTarget?.dogName}
        onConfirm={handleConfirmStopPlan}
      />

      {/* Change Quantity Modal (Legacy Subscriptions) */}
      <ChangeQuantityModal
        open={quantityModalOpen}
        onClose={handleCloseQuantityModal}
        productTitle={quantityTarget?.productTitle || ""}
        variantTitle={quantityTarget?.variantTitle || ""}
        currentQuantity={quantityTarget?.currentQuantity || 1}
        pricePerUnit={quantityTarget?.pricePerUnit || 0}
        onConfirm={handleConfirmQuantityChange}
      />

      {/* Mobile Drawer Nav */}
      <Drawer
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        slotProps={{ paper: { sx: { width: 300, background: QUIZ.color.warmWhite } } }}
      >
        <Box sx={{ height: "100vh" }}>
          <NavContent
            nav={nav}
            setNav={(k) => {
              setNav(k);
              setSidebarOpen(false);
            }}
            collapsed={false}
          />
        </Box>
      </Drawer>

      {/* Global Loading Overlay */}
      <GlobalLoadingOverlay
        visible={globalLoading}
        message={globalLoadingMessage}
      />

      {/* Start-a-plan / resume-plan prompt */}
      <StartPlanPromptModal
        open={
          startPlanPromptMode !== null ||
          (resumePromptForced && resumableSubs.length > 0)
        }
        mode={startPlanPromptMode ?? "resume"}
        pausedPlans={pausedPlanOverviews}
        onLoadDates={handleLoadResumeDates}
        onResume={handleResumePlans}
        onClose={handleDismissResumePrompt}
      />

    </Box>
  );
}

/**
 * Main AccountApp component - wraps with AccountProvider
 */
export function AccountApp({
  customerJSON,
  shopDomain,
  proxyBase,
  products,
}: {
  customerJSON: any;
  shopDomain: string | null;
  proxyBase: string;
  products: any[];
}) {
  return (
    <ErrorBoundary>
      <AccountProvider
        customerJSON={customerJSON}
        shopDomain={shopDomain}
        proxyBase={proxyBase}
        products={products}
      >
        <AccountContent />
      </AccountProvider>
    </ErrorBoundary>
  );
}
